import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import {
  Coins,
  Flame,
  TrendingUp,
  Trophy,
  Sparkles,
  Footprints,
  GraduationCap,
  ShieldCheck,
  ShoppingBag,
  Award,
  ArrowDownLeft,
  ArrowUpRight,
} from "lucide-react";

const ICONS = {
  Sparkles,
  Footprints,
  GraduationCap,
  ShieldCheck,
  Trophy,
  Flame,
  Coins,
  ShoppingBag,
  Award,
};

const REASON_LABEL = {
  signup_bonus: "Sign-up bonus",
  module_complete: "Module complete",
  course_complete: "Course complete",
  quiz_perfect: "Perfect quiz",
  daily_streak: "Daily streak",
  store_purchase: "Store purchase",
  admin_grant: "Admin grant",
};

export default function RewardsPage() {
  const { user } = useAuth();
  const [state, setState] = useState({ balance: 0, streak_days: 0, lifetime_earned: 0, lifetime_spent: 0, transactions: [] });
  const [ach, setAch] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: mwz }, { data: ach }] = await Promise.all([
          api.get("/mwz/me"),
          api.get("/student/achievements"),
        ]);
        setState(mwz);
        setAch(ach);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-6 md:p-10">
      <BackButton />
      <div className="mt-5 mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          /rewards · mowanzi coins
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
          Earn while you learn.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Mowanzi (MWZ) coins are the academy's native reward currency — spend them in the store, on the AI Tutor, or save up for course tuition.
        </p>
      </div>

      {/* Featured MWZ wallet */}
      <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 border-2 border-foreground bg-foreground p-7 text-background hard-shadow relative overflow-hidden" data-testid="mwz-balance-card">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full border-2 border-primary/30"></div>
          <div className="absolute -right-4 -bottom-12 h-32 w-32 rounded-full border-2 border-accent/40"></div>

          <div className="relative">
            <div className="mono text-[10px] uppercase tracking-widest text-background/60">
              Your Mowanzi balance
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display text-6xl font-extrabold tracking-tighter">
                {state.balance.toLocaleString()}
              </div>
              <div className="mono text-lg font-bold text-primary">MWZ</div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-background/20 pt-4">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-background/60">
                  Lifetime earned
                </div>
                <div className="mt-1 font-mono text-lg font-bold">
                  +{state.lifetime_earned.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-background/60">
                  Lifetime spent
                </div>
                <div className="mt-1 font-mono text-lg font-bold">
                  −{state.lifetime_spent.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-background/60">
                  Current streak
                </div>
                <div className="mt-1 flex items-center gap-1 font-mono text-lg font-bold">
                  <Flame className="h-4 w-4 text-accent" />
                  {state.streak_days}d
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How to earn */}
        <div className="border-2 border-foreground bg-background p-6">
          <div className="font-display text-lg font-extrabold tracking-tight">
            How to earn MWZ
          </div>
          <ul className="mt-4 space-y-3 text-sm">
            <EarnRow icon={<Sparkles className="h-4 w-4 text-primary" />} text="Sign-up bonus" reward="+500 MWZ" />
            <EarnRow icon={<Footprints className="h-4 w-4 text-primary" />} text="Complete a module" reward="+1,000 MWZ" />
            <EarnRow icon={<GraduationCap className="h-4 w-4 text-primary" />} text="Complete a course" reward="+5,000 MWZ" />
            <EarnRow icon={<Flame className="h-4 w-4 text-primary" />} text="Daily learning streak" reward="+50 MWZ / day" />
          </ul>
        </div>
      </div>

      {/* Achievements */}
      <div className="mb-10">
        <div className="mb-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /achievements
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            Badges & milestones
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8">
          {ach.map((a) => {
            const Icon = ICONS[a.icon] || Award;
            return (
              <div
                key={a.code}
                className={`border-2 p-4 text-center ${
                  a.earned
                    ? "border-foreground bg-background hard-shadow-sm"
                    : "border-dashed border-foreground/30 bg-muted/40 opacity-60"
                }`}
                data-testid={`achievement-${a.code}`}
              >
                <div
                  className={`mx-auto flex h-10 w-10 items-center justify-center ${
                    a.earned ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-display mt-2 text-xs font-extrabold leading-tight">
                  {a.name}
                </div>
                <div className="mono mt-1 text-[9px] uppercase tracking-widest text-muted-foreground">
                  {a.earned ? "Earned" : "Locked"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* MWZ history */}
      <div>
        <div className="mb-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /history
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            MWZ Transactions
          </h2>
        </div>
        <div className="border-2 border-foreground bg-background">
          <div className="mono grid grid-cols-12 gap-2 border-b border-border bg-muted px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            <div className="col-span-3">Reason</div>
            <div className="col-span-6">Description</div>
            <div className="col-span-2">Time</div>
            <div className="col-span-1 text-right">MWZ</div>
          </div>
          {state.transactions.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No MWZ activity yet.
            </div>
          ) : (
            state.transactions.map((t) => {
              const credit = t.amount >= 0;
              return (
                <div
                  key={t.id}
                  className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-b-0"
                >
                  <div className="col-span-3 flex items-center gap-2">
                    {credit ? <ArrowDownLeft className="h-4 w-4 text-accent" /> : <ArrowUpRight className="h-4 w-4 text-destructive" />}
                    <span className="mono text-[10px] uppercase">{REASON_LABEL[t.reason] || t.reason}</span>
                  </div>
                  <div className="col-span-6">{t.description}</div>
                  <div className="col-span-2 mono text-[10px] text-muted-foreground">
                    {new Date(t.created_at).toLocaleDateString()}
                  </div>
                  <div className={`col-span-1 text-right font-mono font-semibold ${credit ? "text-accent" : "text-destructive"}`}>
                    {credit ? "+" : ""}
                    {t.amount.toLocaleString()}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function EarnRow({ icon, text, reward }) {
  return (
    <li className="flex items-center justify-between border-b border-border pb-2 last:border-b-0">
      <div className="flex items-center gap-2">
        {icon}
        <span>{text}</span>
      </div>
      <span className="mono font-semibold text-primary">{reward}</span>
    </li>
  );
}
